import { useState, useRef, useEffect, useMemo } from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { COUNTRIES } from "@/data/questions";
import { COUNTRY_DIAL_CODES } from "@/data/phone-country-codes";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { getUiTranslations } from "@/lib/ui-translations";
import { getLocalizedOptionLabel } from "@/lib/option-labels";

interface PhoneCountry {
  code: string;
  name: string;
  dialCode: string;
}

// Pin countries most relevant for immigration context
const PINNED_CODES = new Set(["CA", "US", "FR", "MA", "HT"]);

function buildPhoneCountries(): { pinned: PhoneCountry[]; others: PhoneCountry[] } {
  const all: PhoneCountry[] = COUNTRIES
    .filter((c) => COUNTRY_DIAL_CODES[c.code])
    .map((c) => ({ code: c.code, name: c.name, dialCode: COUNTRY_DIAL_CODES[c.code] }));

  return {
    pinned: all.filter((c) => PINNED_CODES.has(c.code)),
    others: all.filter((c) => !PINNED_CODES.has(c.code)),
  };
}

const { pinned: pinnedCountries, others: otherCountries } = buildPhoneCountries();
const allPhoneCountries = [...pinnedCountries, ...otherCountries];

interface PhoneCountrySelectProps {
  /** The currently selected dial code, e.g. "+1" or "+33" */
  value: string;
  onValueChange: (dialCode: string) => void;
  className?: string;
  hasError?: boolean;
  language?: string;
}

export function PhoneCountrySelect({
  value,
  onValueChange,
  className,
  hasError,
  language = 'fr',
}: PhoneCountrySelectProps) {
  const t = getUiTranslations(language);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [highlightIndex, setHighlightIndex] = useState(-1);

  // Resolve selected country from dial code value (strip "+")
  const rawCode = value?.replace("+", "") || "";
  const selectedCountry = useMemo(
    () => allPhoneCountries.find((c) => c.dialCode === rawCode),
    [rawCode],
  );

  const normalize = (s: string) =>
    s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  const q = search ? normalize(search) : "";
  const dialQuery = search.replace("+", "");
  const filtered = search
    ? allPhoneCountries.filter(
        (c) =>
          normalize(c.name).includes(q) ||
          normalize(getLocalizedOptionLabel(c, language)).includes(q) ||
          c.dialCode.includes(dialQuery),
      )
    : [...pinnedCountries, ...otherCountries];

  useEffect(() => { setHighlightIndex(-1); }, [search]);
  useEffect(() => {
    if (open) {
      setSearch("");
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);
  useEffect(() => {
    if (highlightIndex < 0 || !listRef.current) return;
    const items = listRef.current.querySelectorAll("[data-phone-item]");
    items[highlightIndex]?.scrollIntoView({ block: "nearest" });
  }, [highlightIndex]);

  const select = (c: PhoneCountry) => {
    onValueChange(`+${c.dialCode}`);
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && highlightIndex >= 0) {
      e.preventDefault();
      select(filtered[highlightIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const showPinnedSection = !search && pinnedCountries.length > 0;

  const renderItem = (c: PhoneCountry, idx: number) => (
    <button
      key={c.code}
      type="button"
      data-phone-item
      onClick={() => select(c)}
      className={cn(
        "relative flex w-full cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors",
        highlightIndex === idx
          ? "bg-accent text-accent-foreground"
          : "hover:bg-accent hover:text-accent-foreground",
      )}
    >
      <Check
        className={cn(
          "mr-2 h-4 w-4 shrink-0",
          selectedCountry?.code === c.code ? "opacity-100" : "opacity-0",
        )}
      />
      <span className="truncate">{getLocalizedOptionLabel(c, language)}</span>
      <span className="ml-auto pl-2 text-muted-foreground">+{c.dialCode}</span>
    </button>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "flex h-14 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
            hasError && "border-destructive",
            className,
          )}
        >
          <span className={cn("truncate", !selectedCountry && "text-muted-foreground")}>
            {selectedCountry
              ? `${getLocalizedOptionLabel(selectedCountry, language)} (+${selectedCountry.dialCode})`
              : t.selectCountryPlaceholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <div onKeyDown={handleKeyDown}>
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              ref={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t.searchCountry}
              className="flex h-11 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>

          <div ref={listRef} className="max-h-64 overflow-y-auto p-1">
            {filtered.length === 0 && (
              <p className="py-6 text-center text-sm text-muted-foreground">
                {t.noCountryFound}
              </p>
            )}

            {showPinnedSection && (
              <>
                <p className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                  {t.pinnedCountries}
                </p>
                {pinnedCountries.map((c, i) => renderItem(c, i))}
                <div className="-mx-1 my-1 h-px bg-border" />
              </>
            )}

            {(search ? filtered : otherCountries).map((c, i) => {
              const idx = showPinnedSection ? i + pinnedCountries.length : i;
              return renderItem(c, idx);
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
